
import { useAuth } from '@/contexts/AuthContext';
import { IUser } from '@/stack-shared/interfaces/model/user.interface';
import { IUserRole } from '@/stack-shared/interfaces/model/user_role.interface';
import { IRole } from '@/stack-shared/interfaces/model/role.interface';
import { useQuery } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export interface IGetAuthData {
    user: IUser | undefined;
    roles: IRole[]
    userRoles: IUserRole[]
}

// Custom hook to get users and their accessors
export function useAuthData(): IGetAuthData {
    const { user } = useAuth();

    // Fetch roles and userRoles from API
    // We use refresh-auth endpoint which returns roles for the current authenticated user
    const { data: rolesData } = useQuery({
        queryKey: ['user-roles', user?.id],
        queryFn: async () => {
            if (!user?.id) {
                return { roles: [], userRoles: [] };
            }

            try {
                // Use refresh-auth endpoint which returns roles for the current user
                // This endpoint is available to all authenticated users
                const response = await httpClient.get('api/auth/refresh-auth', {});
                
                // Handle both direct response and nested data structure
                const responseData = response.data?.data || response.data;
                
                if (responseData?.success && responseData?.roles) {
                    return {
                        roles: responseData.roles || [],
                        userRoles: responseData.userRoles || []
                    };
                }
                
                // Fallback: check if roles are directly in response
                if (responseData?.roles) {
                    return {
                        roles: responseData.roles || [],
                        userRoles: responseData.userRoles || []
                    };
                }
                
                return { roles: [], userRoles: [] };
            } catch (error) {
                console.error('Failed to fetch user roles:', error);
                return { roles: [], userRoles: [] };
            }
        },
        enabled: !!user,
        staleTime: 10 * 60 * 1000, // Cache for 10 minutes to avoid frequent API calls
        gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    });

    const roles = rolesData?.roles || [];
    const userRoles = (rolesData?.userRoles || []).filter((x: IUserRole) => x.userId === user?.id);

    return {
        user,
        roles,
        userRoles
    };
}

// Legacy function for backward compatibility
export function getAuthData(): IGetAuthData {
    // This is a non-hook version that won't work in components
    // Components should use useAuthData() instead
    throw new Error('getAuthData() is deprecated. Use useAuthData() hook instead.');
}



